import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useToast from 'src/use-cases/toast/useToast';
import { IShipmentOption } from './types';
import { DEFAULT_INVOICE_TAB_KEY, useInvoiceContext } from '../../InvoiceContextProvider';
import { useShipmentSelector } from '../../ShipmentSelector';
import { useRecalculationToastSelector } from '../../recalculationToast/RecalculationToastSelector';

interface UseShipmentActionsProps {
  options: IShipmentOption[];
}

/**
 * Custom hook for managing shipment actions and side effects
 */
export const useShipmentActions = ({ options }: UseShipmentActionsProps) => {
  // Get data from hooks
  const { invoiceSummary, setTabDetails } = useInvoiceContext();
  const { shipmentInvoiceId, changeShipment, shipment } = useShipmentSelector();
  const { confirmAsyncShipment } = useRecalculationToastSelector();
  const openToast = useToast();
  const [searchParams] = useSearchParams();

  // Select shipment handler
  const onSelectShipment = useCallback(
    (newValue: string) => {
      setTabDetails(DEFAULT_INVOICE_TAB_KEY);
      changeShipment(newValue);
    },
    [changeShipment, setTabDetails]
  );

  // Confirm action handler
  const onConfirm = useCallback(async () => {
    await confirmAsyncShipment({
      shipmentInvoiceId: shipment?.shipmentInvoiceId,
      invoiceId: invoiceSummary?.id,
    });
  }, [confirmAsyncShipment, shipment, invoiceSummary]);

  // Copy shipment number handler
  const copyShipmentNumber = useCallback(
    (event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      const selectedOption = options?.find((option) => option.value === shipmentInvoiceId);
      if (selectedOption) {
        navigator.clipboard.writeText(selectedOption?.shipmentNumber ?? '');
        openToast({ toastType: 'success', title: 'Shipment Number copied to clipboard' });
      }
    },
    [options, shipmentInvoiceId, openToast]
  );

  // Auto-select shipment on mount or when options change
  useEffect(() => {
    const shipmentParam = searchParams.get('shipment');
    const shipmentNumberParam = searchParams.get('shipmentNumber');

    if (shipmentParam) {
      // Direct shipmentInvoiceId match (used by search bar)
      changeShipment(shipmentParam);
    } else if (shipmentNumberParam && options?.length > 0) {
      // Match by shipmentNumber (used by analytics Detail button)
      const matchedOption = options?.find((option) => !option.isLabel && option.shipmentNumber === shipmentNumberParam);
      if (matchedOption) {
        changeShipment(matchedOption.value);
      }
    } else if (options?.length > 0) {
      // Default: auto-select first shipment
      const mainOptions = options?.filter((item) => !item?.isLabel);
      if (mainOptions?.length > 0) {
        changeShipment(mainOptions[0]?.value);
      }
    }
  }, [options, changeShipment, searchParams]);

  // Find selected option
  const selectedOption = options?.find((option) => option.value === shipmentInvoiceId);

  return {
    onSelectShipment,
    onConfirm,
    copyShipmentNumber,
    selectedOption,
  };
};
